import type { QueryClient } from "@tanstack/react-query";
import { createContext, type ShouldRevalidateFunction } from "react-router";

import { createApiClient } from "@/api/createApiClient";
import type { Api, ConnectedProjectDto } from "@/api/types";
import { initializeSuperTokens } from "@/authentication/superTokensClient";
import type { Config, ConfigUser } from "@/helpers/config";
import { isLocalAutoLogin } from "@/helpers/local-mock";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { getLocalStorageValue } from "@/hooks/useLocalStorage";
import { sessionIdentityQueryOptions } from "@/queries/authentication/queryOptions";
import { applicationConfigQueryOptions, configurationQueries } from "@/queries/configurations/queryOptions";
import { selfAccountQueries } from "@/queries/self-account/queryOptions";

import { resolveProject } from "./resolveProject";

export interface DashboardStartup {
  queryClient: QueryClient;
  /** Startup requests wait for this, so cookie consent is settled before anything else loads. */
  appReady: Promise<unknown>;
}

/** Gives route loaders the query client. Null in routers created without startup prefetching, such as tests. */
export const dashboardStartupContext = createContext<DashboardStartup | null>(null);

export interface DashboardProjectScope {
  project: ConnectedProjectDto;
  /** Unscoped client, for requests that set the project header themselves. */
  rootApi: Api<null>["api"];
  /** Client scoped to the project, like the one ProjectLoader provides. */
  projectApi: Api<null>["api"];
  /** Reads a config value for the signed-in user, like `useConfig` below `ConfigUserProvider`. */
  getConfigValue: <TKey extends keyof Config>(key: TKey) => Config[TKey];
}

/**
 * Runs `prefetch` once cookie consent is settled and the route's project is known. Route loaders use this to
 * start page queries alongside the session user instead of after it. Prefetches fill the same cache entries the
 * components read; when one fails, the component requests the data itself as before.
 */
export function whenDashboardProjectReady(
  startup: DashboardStartup,
  slug: string | undefined,
  prefetch: (scope: DashboardProjectScope) => void,
): void {
  startup.appReady
    .then(() => resolveDashboardProject(startup.queryClient, slug))
    .then((scope) => {
      if (scope) {
        prefetch(scope);
      }
    })
    .catch(() => {
      // The gates and pages request the same data and show their own errors.
    });
}

/**
 * Starts the authenticated shell's requests as soon as their inputs exist, instead of one render gate at a
 * time. The session check and linked projects run alongside the translations, and the session user and
 * brandings start as soon as the route's project is known.
 */
export function prefetchDashboardStartup(startup: DashboardStartup, slug: string | undefined): void {
  whenDashboardProjectReady(startup, slug, (scope) => {
    const { queryClient } = startup;
    // A failed prefetch leaves the error in the cache; the gate retries when it mounts.
    queryClient.query(selfAccountQueries(scope.rootApi).sessionUserInitial(scope.project.id)).catch(() => {});
    queryClient.query(configurationQueries(scope.projectApi).listAppBrandings()).catch(() => {});
  });
}

/**
 * Page loaders prefetch what the page's first render requests. Search param changes (filters, tabs) are
 * handled by the mounted page, so the loader only needs to run again when the path changes.
 */
export const shouldRevalidateOnPathChange: ShouldRevalidateFunction = ({ currentUrl, nextUrl }) =>
  currentUrl.pathname !== nextUrl.pathname;

/** Resolves the route's project the way ProjectLoader does, from the shared query cache. */
export async function resolveDashboardProject(
  queryClient: QueryClient,
  slug: string | undefined,
): Promise<DashboardProjectScope | undefined> {
  const config = await queryClient.query({ ...applicationConfigQueryOptions(), staleTime: "static" });
  const baseUrl = config.getValue("newCoreApiRootUri");

  let configUser: ConfigUser | undefined;
  if (!isLocalAutoLogin) {
    // Requests only carry the session once the SDK is initialized; without a session the shell redirects to login.
    initializeSuperTokens({
      authenticationServerRootUrl: config.getValue("authenticationServerRootUrl"),
      newCoreApiRootUri: baseUrl,
    });
    const identity = await queryClient.query({ ...sessionIdentityQueryOptions(), staleTime: "static" });
    if (!identity) {
      return undefined;
    }

    const email = identity.emails?.[0];
    configUser = email ? { identifier: identity.sub, email, custom: {} } : undefined;
  }

  const rootApi = createApiClient(baseUrl).api;
  const linkedProjects = await queryClient.query({
    ...selfAccountQueries(rootApi).listLinkedProjects(),
    staleTime: "static",
  });
  const project = resolveProject(linkedProjects, {
    slug,
    recentProjectId: getLocalStorageValue<string | undefined>(LOCAL_STORAGE_KEYS.RECENT_PROJECT, undefined),
  });
  if (!project) {
    return undefined;
  }

  return {
    project,
    rootApi,
    projectApi: createApiClient(baseUrl, { projectId: project.id, appBranding: project.appBranding }).api,
    getConfigValue: (key) => config.getValue(key, undefined, configUser),
  };
}
